// ============================================================================
// IMAGE LIBRARY OPS (Spec D Tasks 25-28)
// ============================================================================
// [CREATED: 2026-05-17]
// [PURPOSE]: Local image storage + Image Book export/import.
//   v1.0 stores base64-encoded images inside playerStats.imageLibrary
//   (already provisioned in Spec D Task 1 schema v4 migration).
//
// [SCOPING]: v1.0 = base64 in playerStats. Future v1.x tiers:
//   Capacitor filesystem (mobile, GB-scale) → IndexedDB (web, ~50MB) →
//   localStorage (~5MB) fallback chain. The shape of imageLibrary
//   doesn't change; only the storage backend swaps.
//
// [SHAPE]: playerStats.imageLibrary = { images: ImageRef[] }
//   ImageRef = {
//     id: string (uuid),
//     kind: 'portrait' | 'map' | 'token' | 'item' | 'entity',
//     name: string,
//     data: string (base64 data URI),
//     tags: string[],
//     addedAt: number (Date.now()),
//     entityId?: string,       // optional binding to a character / monster / item
//     entityKind?: string,     // 'character' | 'monster' | 'item' | 'npc'
//   }
//
// [RULE 18]: writes to playerStats.imageLibrary (not reserved).
//
// [STONE 7 / #72 + #73 + #79 — 2026-05-18]: Expanded the kind enum from
//   {portrait, map} to {portrait, map, token, item, entity} and added
//   optional entityId / entityKind binding so portraits can attach to
//   a specific character record (Audit B6: per-character portrait contract)
//   and tokens / item icons can attach to a specific monster / catalog item.
//   The setAsActivePortrait helper preserves the "Image Library MUST NOT
//   replace character.basics.portrait — integrate, don't parallel" invariant
//   (#79) by writing FROM the library TO basics.portrait, never inverting.
//
// [SPEC]: docs/superpowers/specs/2026-05-17-combat-manager-design.md
// [PLAN]: docs/superpowers/plans/2026-05-17-combat-manager.md Tasks 25-28
// [AUDIT]: docs/superpowers/findings/2026-05-17-compliance-audit/B6-portrait-contract.md

const KNOWN_KINDS = new Set(['portrait', 'map', 'token', 'item', 'entity']);

function normalizeKind(kind) {
  return KNOWN_KINDS.has(kind) ? kind : 'portrait';
}

function genId() {
  return `img_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function withLibrary(prev, updater) {
  const lib = prev.imageLibrary || { images: [] };
  return { ...prev, imageLibrary: updater(lib) };
}

// =============================================================================
// CRUD
// =============================================================================

/** Add an image (portrait / map / token / item / entity). Returns the updater. */
export function addImage({ kind, name, data, tags, entityId, entityKind }) {
  const ref = {
    id: genId(),
    kind: normalizeKind(kind),
    name: String(name || 'Untitled').slice(0, 80),
    data: String(data || ''),
    tags: Array.isArray(tags) ? tags : [],
    addedAt: Date.now(),
  };
  // Only include entity binding fields when explicitly provided — keeps the
  // shape lean for images that aren't entity-bound (shared maps, generic art).
  if (entityId) ref.entityId = String(entityId);
  if (entityKind) ref.entityKind = String(entityKind);
  return (prev) => withLibrary(prev, (lib) => ({
    ...lib,
    images: [...lib.images, ref],
  }));
}

/** Remove an image by id. */
export function removeImage(id) {
  return (prev) => withLibrary(prev, (lib) => ({
    ...lib,
    images: lib.images.filter((img) => img.id !== id),
  }));
}

/** Lookup an image by id. */
export function findImage(playerStats, id) {
  if (!playerStats?.imageLibrary || !id) return null;
  return playerStats.imageLibrary.images.find((img) => img.id === id) || null;
}

/** Filter images by kind. */
export function imagesByKind(playerStats, kind) {
  if (!playerStats?.imageLibrary) return [];
  return playerStats.imageLibrary.images.filter((img) => img.kind === kind);
}

/**
 * Filter images by entity binding (Stone 7 / #73).
 * Returns images bound to `entityId`. Optional second arg narrows further
 * by entityKind ('character' / 'monster' / 'item' / 'npc').
 *
 * @param {Object} playerStats
 * @param {string} entityId
 * @param {string} [entityKind]
 * @returns {ImageRef[]}
 */
export function imagesByEntity(playerStats, entityId, entityKind) {
  if (!playerStats?.imageLibrary || !entityId) return [];
  return playerStats.imageLibrary.images.filter((img) => {
    if (img.entityId !== entityId) return false;
    if (entityKind && img.entityKind !== entityKind) return false;
    return true;
  });
}

/**
 * "Set as Active Portrait" — Stone 7 / #79.
 *
 * Writes the library image's data to `character.basics.portrait` (and the
 * legacy mirror `character.portrait`). The library entry stays put — this
 * is the integration point between the gallery and the per-character
 * portrait contract, NOT a replacement.
 *
 * Constraints:
 *  - Only `kind: 'portrait'` images may be promoted (a map / token / item
 *    icon would not make sense as a face).
 *  - Unknown id is a no-op (no clobber).
 *  - Missing `basics` shape is initialized rather than crashing.
 *
 * @param {string} imageId
 * @returns {(prev: Object) => Object} setPlayerStats updater
 */
export function setAsActivePortrait(imageId) {
  return (prev) => {
    const img = findImage(prev, imageId);
    if (!img || img.kind !== 'portrait') {
      // No clobber on miss or wrong kind — preserves the invariant that the
      // library cannot accidentally replace basics.portrait with a non-portrait.
      const safe = { ...prev };
      if (!safe.basics) safe.basics = { portrait: '' };
      else if (safe.basics.portrait === undefined) safe.basics = { ...safe.basics, portrait: '' };
      return safe;
    }
    return {
      ...prev,
      basics: { ...(prev.basics || {}), portrait: img.data },
      // Legacy mirror — kept in lockstep until imageGallery sunset (audit #99).
      portrait: img.data,
    };
  };
}

// =============================================================================
// FILE → BASE64 (browser File API)
// =============================================================================

/**
 * Read a File / Blob to a base64 data URI string.
 * Returns a Promise<string>. Used by the upload UI.
 *
 * @param {File|Blob} file
 * @returns {Promise<string>}
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error('No file provided'));
    if (typeof FileReader === 'undefined') {
      return reject(new Error('FileReader API not available in this environment'));
    }
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
}

// =============================================================================
// IMAGE BOOK EXPORT / IMPORT
// =============================================================================

const IMAGE_BOOK_VERSION = 1;

/**
 * Build an Image Book bundle (`.imagebook.json` payload).
 *
 * @param {Object} playerStats
 * @param {string} [label='My Image Library']
 * @returns {Object}
 */
export function buildImageBook(playerStats, label = 'My Image Library') {
  const images = playerStats?.imageLibrary?.images || [];
  return {
    version: IMAGE_BOOK_VERSION,
    exportedAt: new Date().toISOString(),
    label,
    images: images.map((img) => ({
      id: img.id,
      kind: img.kind,
      name: img.name,
      data: img.data,
      tags: img.tags || [],
      // Stone 7 #72/#73: entity binding fields round-trip through the book.
      ...(img.entityId ? { entityId: img.entityId } : {}),
      ...(img.entityKind ? { entityKind: img.entityKind } : {}),
    })),
  };
}

/**
 * Import an Image Book bundle. Re-keys ids to avoid collisions with
 * existing library entries. Tolerates partial/malformed bundles
 * gracefully — drops invalid entries instead of throwing.
 *
 * @param {Object} bundle — the parsed .imagebook.json
 * @returns {Function} setPlayerStats updater
 */
export function importImageBook(bundle) {
  return (prev) => withLibrary(prev, (lib) => {
    if (!bundle || !Array.isArray(bundle.images)) return lib;
    const existing = new Set(lib.images.map((img) => img.id));
    const added = [];
    for (const entry of bundle.images) {
      if (!entry || typeof entry !== 'object') continue;
      if (typeof entry.data !== 'string' || entry.data.length === 0) continue;
      let id = entry.id || genId();
      // Re-key on collision.
      while (existing.has(id)) {
        id = genId();
      }
      const ref = {
        id,
        kind: normalizeKind(entry.kind),
        name: String(entry.name || 'Imported').slice(0, 80),
        data: entry.data,
        tags: Array.isArray(entry.tags) ? entry.tags : [],
        addedAt: Date.now(),
      };
      // Round-trip entity binding (Stone 7 #72/#73).
      if (entry.entityId) ref.entityId = String(entry.entityId);
      if (entry.entityKind) ref.entityKind = String(entry.entityKind);
      added.push(ref);
      existing.add(id);
    }
    return { ...lib, images: [...lib.images, ...added] };
  });
}

/**
 * Estimate the library's total base64 byte size. Used for the
 * "warn at 20MB / hard-cap at platform limit" UX surface.
 *
 * @param {Object} playerStats
 * @returns {number} bytes (approximate)
 */
export function libraryBytes(playerStats) {
  const images = playerStats?.imageLibrary?.images || [];
  let total = 0;
  for (const img of images) {
    // Base64 expansion is ~4/3 of raw bytes; len(string) is close enough.
    total += (img.data || '').length;
    total += (img.name || '').length;
    total += (img.tags || []).reduce((s, t) => s + (t || '').length, 0);
  }
  return total;
}
